import { jsonCopyObject, sortByPropOfNumber } from "../helpers/helpers";

export interface Page {
  page: number;
  id: string
}

export interface PageUpdate<T> {
  page: number;
  id: string;
  update: { [x: string]: T }
}

export interface PageUpdateData<T> {
  updates: PageUpdate<T>[]
  accountId?: string // accountId for account page
  eventId?: string // eventId for event page
}

export const deleteFromPages = <P extends Page, DICTIONARYKEY extends keyof P>(
  pages: P[],
  key: DICTIONARYKEY,
  idsToDelete: string[],
  deletionFunction: any,
) => {
  console.log({ pages, key, idsToDelete })
  const changes: { [pageId: string]: Partial<P> } = {}
  idsToDelete.forEach(id => {
    const page = pages.find(p => !!p[key][id])
    if (!page) {
      return // shouldn't happen but maybe timing issues, better to just silently fail
    }
    const textKey = `${key as string}.${id}`
    if (!changes[page.id]) changes[page.id] = {}
    changes[page.id][textKey] = deletionFunction()
  })
  return changes
}

export const updateInPages = <P extends Page, DICTIONARYKEY extends keyof P, T extends { id?: string }>(
  pages: P[],
  key: DICTIONARYKEY,
  updates: T[],
  createNewPage: (page: number) => P,
  maxSize: number,
  deletionFunction: any
) => {

  pages.sort(sortByPropOfNumber('page')) // should try and fill newest pages first, somewhat of an optimization
  const newPages: P[] = []
  const tooLargePageIds: Set<string> = new Set()
  const changes: { [pageId: string]: { [key:string]: T } } = {} // includes deletions, no need to type as deletionFunction is any
  
  // console.log({ key, updates })
  updates.forEach(update => {
    const textKey = `${key as string}.${update.id}`

    let page = pages.find(p => !!p[key][update.id])
    // console.log({ page1: page })

    if (!page && pages.length) page = pages[pages.length - 1]
    // console.log({ page2: page })

    if (page) {
      if (!changes[page.id]) changes[page.id] = {}
      const testPage = jsonCopyObject(page)
      testPage[key][update.id] = update
      const tooLarge = JSON.stringify(testPage).length > maxSize
      if (tooLarge) { // need logic for inserting stuff into old pages ************** ************ <===
        changes[page.id][textKey] = deletionFunction() // deletes only a given entity
        tooLargePageIds.add(page.id)
        page = null
      } else {
        changes[page.id][textKey] = update // replaces or sets a given entity
        return // ends ends change part of for each loop
      }
    }

    while (tooLargePageIds.size < pages.length) { // now try to fit into another page. Only happens once per page
      const possiblePage = pages.find(p => !tooLargePageIds.has(p.id)) // since pages is now in reverse order will try to update newer pages first.  
      const testPage = jsonCopyObject(possiblePage)
      testPage[key][update.id] = update
      const tooLarge = JSON.stringify(testPage).length > maxSize
      if (!tooLarge) {
        page = possiblePage
        if (!changes[page.id]) changes[page.id] = {}
        changes[page.id][textKey] = update // replaces or sets a given entity
        return // ends change part of for each loop
      } else {
        tooLargePageIds.add(possiblePage.id)
      }
    }

    // Finally, use new pages.  This should only be hit if the update to all previous pages failed for at least one entity (so yes, one single entity could mess up this logic but oh well)
    if (!page) { // don't change to else!!!  New page logic - No page means need to move into new page
      if (!newPages.length) newPages.push(createNewPage(pages.length + 1))
      let newPage = newPages[newPages.length - 1]
      // console.log({ newPage1: newPage })
      const testNewPage = jsonCopyObject(newPage)
      testNewPage[key][update.id] = update
      const tooLarge = JSON.stringify(testNewPage).length > maxSize

      if (tooLarge) {
        newPages.push(createNewPage(pages.length + newPages.length + 1))
        newPage = newPages[newPages.length - 1]
      }
      // console.log({ newPage2: newPage })
      newPage[key][update.id] = update // directly set on new page for new pages
    }


  })
  return { changes, newPages }
}
